import { useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Paper,
    Typography
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { useAuth } from '../../hooks/use-auth';
import { useOnboarding } from './use-onboarding';
import { startOnboardingTour } from './onboarding-tour-host';
import { hasOnboardingStarted } from './onboarding-config';

interface OnboardingWidgetProps {
    /** From the layout's existing useCompanySetup() — avoids a duplicate API call. */
    hasCompany: boolean | null;
}

// Sits above the chat Fab (bottom: 24, right: 24, height 56) in dashboard-layout.
const ANCHOR = { position: 'fixed', bottom: 96, right: 24, zIndex: 1100 } as const;

export const OnboardingWidget: FC<OnboardingWidgetProps> = ({ hasCompany }) => {
    const navigate = useNavigate();
    const { isAuthenticated, projects } = useAuth() as any;
    const {
        steps,
        screens,
        completion,
        visited,
        visitedCount,
        screenTotal,
        completedCount,
        total,
        allComplete,
        dismissed,
        dismiss
    } = useOnboarding({ hasCompany, projects: Array.isArray(projects) ? projects : [] });

    const [open, setOpen] = useState(true);

    if (!isAuthenticated || dismissed) {
        return null;
    }

    // Users already set up before launch never started the checklist — don't surface anything.
    if (allComplete && !hasOnboardingStarted()) {
        return null;
    }

    // Compact success state once every step is done — closing just hides it.
    if (allComplete) {
        return (
            <Paper elevation={8} sx={{ ...ANCHOR, width: 320, p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        🎉 You&apos;re all set!
                    </Typography>
                    <IconButton size="small" onClick={dismiss} aria-label="Close onboarding">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="textSecondary">
                    Your workspace is ready. Dive into your project anytime.
                </Typography>
            </Paper>
        );
    }

    // Collapsed launcher pill — always available until 100% (no skip).
    if (!open) {
        return (
            <Button
                variant="contained"
                color="primary"
                startIcon={<EmojiObjectsIcon />}
                onClick={() => setOpen(true)}
                sx={{ ...ANCHOR, borderRadius: 8, boxShadow: 6 }}
            >
                Get started · {completedCount}/{total}
            </Button>
        );
    }

    const progress = total > 0 ? (completedCount / total) * 100 : 0;

    return (
            <Paper elevation={8} sx={{ ...ANCHOR, width: 340, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">Get started</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Your first project in 10 min
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Minimize onboarding">
                            <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                            {completedCount}/{total}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ px: 1, pb: 1.5, maxHeight: 360, overflowY: 'auto' }}>
                    {steps.map((s) => {
                        const done = completion[s.id];
                        const isExplore = s.id === 'explore';
                        return (
                            <Box
                                key={s.id}
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: 1 }}
                            >
                                {done ? (
                                    <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                                ) : (
                                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" sx={{ mt: 0.25 }} />
                                )}
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            textDecoration: done ? 'line-through' : 'none',
                                            color: done ? 'text.secondary' : 'text.primary'
                                        }}
                                    >
                                        {s.title}
                                        {isExplore && ` (${visitedCount}/${screenTotal})`}
                                    </Typography>
                                    {!done && (
                                        <>
                                            <Typography variant="caption" color="textSecondary" component="p">
                                                {s.description}
                                            </Typography>
                                            {isExplore ? (
                                                <>
                                                    <Box sx={{ mt: 1, mb: 0.5 }}>
                                                        {screens.map((screen) => (
                                                            <Box
                                                                key={screen.key}
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
                                                            >
                                                                {visited.includes(screen.key) ? (
                                                                    <CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
                                                                ) : (
                                                                    <RadioButtonUncheckedIcon color="disabled" sx={{ fontSize: 14 }} />
                                                                )}
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {screen.label}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                    <Button size="small" variant="contained" onClick={startOnboardingTour} sx={{ mt: 0.5 }}>
                                                        {s.ctaLabel}
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => navigate(s.route)}
                                                    sx={{ mt: 0.5, px: 0 }}
                                                >
                                                    {s.ctaLabel}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Paper>
    );
};
